import {
  Alert,
  Box,
  Button,
  Checkbox,
  Drawer,
  Group,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconSearch } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DataTable,
  EmptyState,
  ErrorState,
  ExportMenu,
  InlineError,
  LoadingSkeleton,
  Metric,
  NumField,
  SectionCard,
  StateBadge,
  useColumns,
  useSlashToFocus,
} from "../../components/ui";
import { api, type StockLevel, type StockSummary } from "../../lib/api";
import { Page } from "../../shell/Page";

/**
 * Stock on hand, with the low-stock list the Store Manager acts on.
 *
 * Also where a correction is entered, because a discrepancy is noticed while
 * looking at this list - and a correction takes a delta and a reason, never a
 * replacement quantity. Typing "what I counted" would silently discard any
 * bill raised while the count was being walked.
 */
export function StockLevels() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<StockLevel[]>();
  const [summary, setSummary] = useState<StockSummary>();
  const [query, setQuery] = useState("");
  const [onlyLow, setOnlyLow] = useState(false);
  const [error, setError] = useState<string>();

  const [openId, setOpenId] = useState<string>();
  const [delta, setDelta] = useState<number | string>("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  useSlashToFocus(searchRef);

  async function load() {
    try {
      setError(undefined);
      const result = await api.stockLevels({
        ...(query.trim() ? { query: query.trim() } : {}),
        onlyLow,
      });
      setProducts(result.products);
      setSummary(result.summary);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load stock.");
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyLow]);

  function openCorrection(product: StockLevel) {
    setOpenId(openId === product.id ? undefined : product.id);
    setDelta("");
    setReason("");
  }

  async function correct(product: StockLevel) {
    const amount = Number(delta);
    if (!Number.isInteger(amount) || amount === 0) return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await api.correctStock({
        productId: product.id,
        quantityDelta: amount,
        reason: reason.trim(),
      });
      notifications.show({
        color: "teal",
        message:
          `${product.sku} corrected by ${amount > 0 ? "+" : ""}${amount} — ` +
          `now ${result.movement.runningBalanceAfter}.`,
      });
      setOpenId(undefined);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not apply that.");
    } finally {
      setBusy(false);
    }
  }

  // Still needed by the confirmation, which states the arithmetic out loud.
  // Whether the delta is USABLE is the form's business now - it owns the
  // fields, so it owns what counts as a valid value in them.
  const amount = Number(delta);

  /**
   * A correction has no supplier invoice or bill behind it, so it is the one
   * stock movement with nothing to check it against. Confirming states the
   * arithmetic - what it is now, what it becomes - because a mistyped sign is
   * the easy mistake here and the ledger is append-only: it can be corrected
   * again but never taken back.
   */
  function confirmCorrection(product: StockLevel) {
    modals.openConfirmModal({
      title: `Correct ${product.sku}`,
      children: (
        <Stack gap="xs">
          <Text fz="md">
            {product.currentStockQty} becomes{" "}
            <strong>{product.currentStockQty + amount}</strong> (
            {amount > 0 ? "+" : ""}
            {amount}).
          </Text>
          <Text fz="md" c="gray.7">
            The ledger is append-only, so this is recorded as a movement with
            your reason against it. It can be corrected again, but not undone.
          </Text>
        </Stack>
      ),
      labels: { confirm: "Apply correction", cancel: "Not yet" },
      onConfirm: () => void correct(product),
    });
  }

  /** The row the drawer is editing, if any. */
  const correcting = products?.find((product) => product.id === openId);

  const columns = useColumns<StockLevel>(
    () => [
      {
        key: "SKU",
        header: "SKU",
        width: "220px",
        render: (product) => (
          <Text fz={13} fw={600} className="num" truncate>
            {product.sku}
          </Text>
        ),
      },
      {
        key: "Product",
        header: "Product",
        width: "auto",
        render: (product) => (
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Text fz={14} truncate>
              {product.name}
            </Text>
            {product.category && (
              <Text fz={12} c="gray.6" truncate>
                {product.category.name}
              </Text>
            )}
          </Stack>
        ),
      },
      {
        key: "On hand",
        header: "On hand",
        width: "110px",
        align: "right",
        render: (product) => (
          <Text
            fz={15}
            fw={product.currentStockQty === 0 ? 700 : 600}
            className="num"
            c={product.currentStockQty === 0 ? "red.8" : undefined}
          >
            {product.currentStockQty}
          </Text>
        ),
      },
      {
        key: "Reorder at",
        header: "Reorder at",
        width: "120px",
        align: "right",
        // "not set" is NOT zero, and must not be shown as it: a product with
        // no threshold can only ever show as out of stock, never as low.
        render: (product) =>
          product.reorderLevel === null ? (
            <Text fz={13} c="gray.6">
              not set
            </Text>
          ) : (
            <Text fz={14} className="num">
              {product.reorderLevel}
            </Text>
          ),
      },
      {
        key: "Status",
        header: "Status",
        width: "170px",
        // A label, never a bare red number: "out of stock" is the fact, and
        // colour on its own is not a fact anybody can read aloud.
        render: (product) =>
          product.outOfStock ? (
            <StateBadge state="dlvFailed" label="Out of stock" />
          ) : product.belowReorder ? (
            <StateBadge state="dlvPending" label="Below reorder" />
          ) : (
            <Text fz={13} c="gray.6">
              Fine
            </Text>
          ),
      },
      {
        key: "action",
        header: "",
        width: "210px",
        render: (product) => (
          <Group gap={6} wrap="nowrap">
            <Button
              variant="default"
              size="compact-sm"
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/inventory/movements?productId=${product.id}`);
              }}
            >
              History
            </Button>
            <Button
              variant={openId === product.id ? "light" : "default"}
              size="compact-sm"
              onClick={(event) => {
                event.stopPropagation();
                openCorrection(product);
              }}
            >
              {openId === product.id ? "Correcting" : "Correct count"}
            </Button>
          </Group>
        ),
        // No Receive button. The brief offers one "where relevant", but goods
        // are received per delivery note, not per product, and the stock-in
        // screen picks its own lines - a button that jumped there with
        // nothing selected would promise more than it does.
      },
    ],
    [openId, navigate],
  );

  return (
    <Page
      title="Stock on hand"
      subtitle={
        summary
          ? `${summary.unitsOnHand.toLocaleString("en-IN")} units across ${summary.total} products`
          : undefined
      }
      back="/inventory"
      width="wide"
    >
      {error && !products && <ErrorState message={error} onRetry={() => void load()} />}
      {error && products && <InlineError>{error}</InlineError>}

      {summary && (summary.outOfStock > 0 || summary.belowReorder > 0) && (
        <Alert
          variant="light"
          color={summary.outOfStock > 0 ? "red" : "yellow"}
          radius="md"
          icon={<IconAlertTriangle size={18} stroke={1.8} />}
        >
          {summary.outOfStock > 0 && `${summary.outOfStock} out of stock`}
          {summary.outOfStock > 0 && summary.belowReorder > 0 && " · "}
          {summary.belowReorder > 0 && `${summary.belowReorder} below reorder level`}
        </Alert>
      )}

      <SectionCard title="Find a product">
        <Stack gap="sm">
          <Group gap="sm" align="flex-end" wrap="nowrap">
            <TextInput
              ref={searchRef}
              id="stock-search"
              label="Search"
              type="search"
              value={query}
              placeholder="SKU or name"
              leftSection={<IconSearch size={16} stroke={1.8} />}
              style={{ flex: 1 }}
              onChange={(event) => setQuery(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void load();
              }}
            />
            <Button variant="default" onClick={() => void load()}>
              Find
            </Button>
          </Group>

          <Checkbox
            id="only-low"
            label="Only what needs attention"
            checked={onlyLow}
            onChange={(event) => setOnlyLow(event.currentTarget.checked)}
          />
          {summary && summary.withoutReorderLevel > 0 && (
            <Text fz="sm" c="gray.6">
              {summary.withoutReorderLevel} product(s) have no reorder level
              set, so they can only show as out of stock, never as low. Set one
              from the price list screen.
            </Text>
          )}
        </Stack>
      </SectionCard>

      {!products && !error && <LoadingSkeleton variant="card" count={4} />}

      {products?.length === 0 && (
        <EmptyState
          title={onlyLow ? "Nothing needs attention" : "No products match that"}
        />
      )}

      {/* Desktop: the brief's table. There is deliberately NO editable
          quantity column - a count is entered as a delta with a reason, in
          the drawer that "Correct count" opens - and the footnote below says
          so, because an absence explains nothing on its own. */}
      {products && products.length > 0 && (
        <Box visibleFrom="md">
          <Stack gap={12}>
            <Group justify="flex-end">
              <ExportMenu dataset="stock-levels" />
            </Group>
            <DataTable
              rows={products}
              columns={columns}
              rowKey={(product) => product.id}
              onOpen={(product) => openCorrection(product)}
              activeKey={openId}
              caption={`Stock on hand: ${products.length} products`}
            />
            <Text fz={12} c="gray.6">
              There is deliberately no editable quantity column. A count is
              entered as a delta with a reason, in the drawer that “Correct
              count” opens. j / k or the arrow keys move, Enter opens the
              drawer, / searches.
            </Text>
          </Stack>
        </Box>
      )}

      <Drawer
        opened={openId !== undefined}
        onClose={() => setOpenId(undefined)}
        position="right"
        size="min(380px, 100%)"
        withOverlay={false}
        title={correcting ? `Correct ${correcting.sku}` : "Correct count"}
        styles={{ content: { boxShadow: "0 2px 8px rgba(33,37,41,.08)" } }}
      >
        {correcting && (
          <Stack gap="sm">
            <Text fz="sm" c="gray.7">
              {correcting.name}
            </Text>
            <CorrectionForm
              key={correcting.id}
              product={correcting}
              delta={delta}
              setDelta={setDelta}
              reason={reason}
              setReason={setReason}
              busy={busy}
              onApply={() => confirmCorrection(correcting)}
            />
          </Stack>
        )}
      </Drawer>

      {/* Phone: the cards, with the same form inline. */}
      <Box hiddenFrom="md">
      <Stack gap="md">
      {products?.map((product) => (
        <SectionCard key={product.id} title={product.sku}>
          <Stack gap="sm">
            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
              <Stack gap={2} style={{ minWidth: 0 }}>
                <Text fz="lg" fw={600} c="gray.9">
                  {product.name}
                </Text>
                {product.category && (
                  <Text fz="sm" c="gray.6">
                    {product.category.name}
                  </Text>
                )}
              </Stack>
              <Stack gap={0} align="flex-end" style={{ flexShrink: 0 }}>
                <Metric value={product.currentStockQty} size="kpi" />
                <Text fz="xs" c="gray.6">
                  {product.reorderLevel === null
                    ? "no reorder level"
                    : `reorder at ${product.reorderLevel}`}
                </Text>
              </Stack>
            </Group>

            {/* A label, not just a red number: "out of stock" is the fact. */}
            {product.outOfStock ? (
              <Group>
                <StateBadge state="dlvFailed" label="Out of stock" />
              </Group>
            ) : product.belowReorder ? (
              <Group>
                <StateBadge state="dlvPending" label="At or below reorder level" />
              </Group>
            ) : null}

            <Group gap="sm">
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate(`/inventory/movements?productId=${product.id}`)}
              >
                History
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => openCorrection(product)}
              >
                {openId === product.id ? "Cancel" : "Correct count"}
              </Button>
            </Group>

            {openId === product.id && (
              <CorrectionForm
                product={product}
                delta={delta}
                setDelta={setDelta}
                reason={reason}
                setReason={setReason}
                busy={busy}
                onApply={() => confirmCorrection(product)}
              />
            )}
          </Stack>
        </SectionCard>
      ))}
      </Stack>
      </Box>
    </Page>
  );
}

/**
 * The correction form: a signed delta and a reason, nothing else.
 *
 * One component, rendered inline in the phone's card and inside the desktop
 * drawer. It was written inline in the card first; the desktop table needed
 * the same fields, and two copies of a form whose helper text is the whole
 * point - "the difference, not the new total" - is two chances for one of
 * them to stop saying it.
 */
function CorrectionForm({
  product,
  delta,
  setDelta,
  reason,
  setReason,
  busy,
  onApply,
}: {
  product: StockLevel;
  delta: number | string;
  setDelta: (value: number | string) => void;
  reason: string;
  setReason: (value: string) => void;
  busy: boolean;
  onApply: () => void;
}) {
  const amount = Number(delta);
  const deltaValid = Number.isInteger(amount) && amount !== 0;
  const wouldBe = product.currentStockQty + amount;

  return (
    <Stack gap="sm">
      <NumField
        id={`delta-${product.id}`}
        label="Change by"
        placeholder="e.g. -3 or 5"
        step={1}
        allowDecimal={false}
        value={delta}
        onChange={setDelta}
        description={
          deltaValid
            ? `The difference, not the new total — so a bill raised while you were counting is not wiped out. That makes it ${wouldBe}.`
            : "The difference, not the new total — so a bill raised while you were counting is not wiped out."
        }
        error={
          deltaValid && wouldBe < 0
            ? `That would leave ${wouldBe}. Stock cannot go below zero.`
            : undefined
        }
      />

      <TextInput
        id={`reason-${product.id}`}
        label="Why it differs"
        withAsterisk
        maxLength={500}
        placeholder="e.g. damaged in the warehouse"
        value={reason}
        onChange={(event) => setReason(event.currentTarget.value)}
        description="A correction has no supplier invoice or bill behind it, so this note is the only record of why."
      />

      <Button
        size="md"
        loading={busy}
        disabled={busy || !deltaValid || reason.trim().length < 3 || wouldBe < 0}
        onClick={onApply}
      >
        Apply correction
      </Button>
      <Text fz={12} c="gray.6">
        The ledger is append-only. This can be corrected again, but never
        undone.
      </Text>
    </Stack>
  );
}
